import { CsatScale } from "../../store/definitions";

/*
 * Domínio das 4 escalas de CSAT (v2). Cada opção mapeia para um score CANÔNICO inteiro 1..5 — é isso
 * (e só isso) que vai no POST /api/csat; o backend deriva o rawScore do canônico (a escala é snapshot
 * na linha). Strategy map por tipo (mesmo padrão de components/prechat/fieldBehaviors.ts).
 *
 * - numbers / stars / faces = 5 pontos diretos (score = posição 1..5).
 * - thumbs = 2 pontos: 👍 = 5, 👎 = 1 (por isso o POST em thumbs só aceita score ∈ {1,5}).
 *
 * `label` = glyph exibido; `ariaLabel` = texto pt-BR (seletor estável + leitor de tela). O JSX/estilo
 * é do CsatCard (F6a); aqui é só o modelo + o guard.
 */
export type CsatOption = {
    score: number; // canônico 1..5
    label: string; // glyph exibido
    ariaLabel: string; // pt-BR, a11y
};

const FACE_GLYPHS = ["😡", "🙁", "😐", "🙂", "😀"];
const FACE_LABELS = ["Muito insatisfeito", "Insatisfeito", "Neutro", "Satisfeito", "Muito satisfeito"];

const fivePoint = (glyph: (score: number) => string, aria: (score: number) => string): CsatOption[] =>
    [1, 2, 3, 4, 5].map((score) => ({ score, label: glyph(score), ariaLabel: aria(score) }));

export const CSAT_SCALE_OPTIONS: Record<CsatScale, CsatOption[]> = {
    numbers: fivePoint(
        (n) => String(n),
        (n) => `Nota ${n} de 5`
    ),
    stars: fivePoint(
        () => "★",
        (n) => `${n} de 5 estrelas`
    ),
    faces: fivePoint(
        (n) => FACE_GLYPHS[n - 1],
        (n) => FACE_LABELS[n - 1]
    ),
    thumbs: [
        { score: 5, label: "👍", ariaLabel: "Gostei" },
        { score: 1, label: "👎", ariaLabel: "Não gostei" }
    ]
};

/*
 * S1 — guard de validade do score canônico antes do POST (defesa em profundidade; a UI nunca produz
 * inválido, mas isto blinda o handler). Inteiro 1..5; em `thumbs`, só {1,5} (score=3 em thumbs = inválido,
 * espelha o 400 do backend).
 */
export function isValidCanonicalScore(score: number, scale: CsatScale): boolean {
    if (!Number.isInteger(score) || score < 1 || score > 5) {
        return false;
    }
    if (scale === "thumbs") {
        return score === 1 || score === 5;
    }
    return true;
}
